import { inject, injectable } from "inversify";
import type { IService } from "@designboticteam/core";
import { Logger } from "@designboticteam/core";
import { BuildingLineTool } from "../../tools/BuildingLineTool";
import { ProjectAreaTool } from "../../tools/ProjectAreaTool";
import { ZoneTool } from "../../tools/ZoneTool";
import type { KolegaSnapshot } from "./types";

@injectable()
class SnapshotService implements IService {
	public readonly serviceName = "KolegaSnapshotService";
	private readonly logger = new Logger(`📸 ${this.serviceName}`, { color: "#ffffff", backgroundColor: "#1d4ed8" });

	constructor(
		@inject(ProjectAreaTool) private readonly projectAreaTool: ProjectAreaTool,
		@inject(ZoneTool) private readonly zoneTool: ZoneTool,
		@inject(BuildingLineTool) private readonly buildingLineTool: BuildingLineTool,
	) {}

	public initialize(): void {
		this.logger.log("initialized");
	}

	public dispose(): void {
		this.logger.log("disposed");
	}

	public getSnapshot(): KolegaSnapshot {
		return {
			projectAreaTool: this.createProjectAreaToolSnapshot(),
			zoneTool: this.createZoneToolSnapshot(),
			buildingLineTool: this.createBuildingLineToolSnapshot(),
		};
	}

	private createProjectAreaToolSnapshot(): KolegaSnapshot["projectAreaTool"] {
		const projectArea = this.projectAreaTool.getProjectArea();

		if (projectArea === null) {
			return { projectArea: null };
		}

		const center = projectArea.getCenter();
		const radius = projectArea.getRadius();

		if (center === null || radius <= 0) {
			this.logger.warn("Invalid project area:", { projectArea });
			return { projectArea: null };
		}

		return {
			projectArea: {
				center: center.toArray(),
				radius,
			},
		};
	}

	private createZoneToolSnapshot(): KolegaSnapshot["zoneTool"] {
		const zones = this.zoneTool.getZones();

		return {
			zones: zones.map((zone) => ({
				id: crypto.randomUUID(),
				zoneType: zone.getZoneType(),
				points: zone.getPoints().map((v) => v.toArray()),
			})),
		};
	}

	private createBuildingLineToolSnapshot(): KolegaSnapshot["buildingLineTool"] {
		const buildingLines = this.buildingLineTool.getBuildingLines();

		return {
			buildingLines: buildingLines.map((buildingLine) => ({
				id: crypto.randomUUID(),
				points: buildingLine.getPoints().map((v) => v.toArray()),
			})),
		};
	}
}

export { SnapshotService };
